import classNames from 'classnames';
import Image from 'next/image';
import { type IntlShape, useIntl } from 'react-intl';
import type { Size } from '../../types';
import flairManagerSrc from './images/flair-manager.svg';
import flairPeakSrc from './images/flair-peak.svg';
import flairPlusSrc from './images/flair-plus.svg';
import styles from './Avatar.module.scss';

export type Flair = 'manager' | 'plus' | 'peak';

const getSrc = (flair: Flair) => {
  switch (flair) {
    case 'manager':
      return flairManagerSrc;
    case 'plus':
      return flairPlusSrc;
    case 'peak':
      return flairPeakSrc;
  }
};

const getAriaLabel = (flair: Flair, intl: IntlShape) => {
  switch (flair) {
    case 'manager':
      return intl.formatMessage({ defaultMessage: 'Manager' });
    case 'plus':
      return intl.formatMessage({ defaultMessage: 'Plus' });
    case 'peak':
      return intl.formatMessage({ defaultMessage: 'Peak' });
  }
};

const getSize = (size: Size<'xs' | 'sm' | 'md' | 'lg' | 'xl'>) => {
  switch (size) {
    case 'xs':
      return 12;
    case 'sm':
    case 'md':
      return 16;
    case 'lg':
    case 'xl':
      return 28;
  }
};

type AvatarFlairProps = {
  flair: Flair;
  size: Size<'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'>;
};

const AvatarFlair = ({ flair, size }: AvatarFlairProps) => {
  const intl = useIntl();

  if (size === 'xxs') {
    return null;
  }

  // The SVGs pad their 12x12 artwork by 1 unit on each side so the white stroke isn't clipped by the image bounds
  const imageSize = (getSize(size) * 14) / 12;

  return (
    <Image
      alt={getAriaLabel(flair, intl)}
      className={classNames(styles.flair, styles[flair], styles[size])}
      height={imageSize}
      src={getSrc(flair)}
      width={imageSize}
    />
  );
};

export default AvatarFlair;
